import type { DirectiveBinding, ObjectDirective } from '@/composition/vue/compositionApi'
import { useTooltipManager } from '@/composition/tooltip/useTooltipManager'
import type { TmTooltipDirectiveBinding, TmTooltipDirectiveData } from '@/components/shared/tooltip/types'

const { registerTooltip, unregisterTooltip, isRegisteredTooltip, updateTooltipData } = useTooltipManager()

type ElState = {
  data: TmTooltipDirectiveData
  onMouseEnter: () => void
}

const elStates = new WeakMap<HTMLElement, ElState>()

const attach = (el: HTMLElement, data: TmTooltipDirectiveData) => {
  const state: ElState = {
    data,
    onMouseEnter: () => {
      if (!isRegisteredTooltip(el)) {
        registerTooltip(el, state.data)
      }
    },
  }

  elStates.set(el, state)

  if (!data.persistence) {
    el.addEventListener('mouseenter', state.onMouseEnter)
  }
  // if v-tooltip has persistence.isOpen=true during the mounting, register the tooltip immediately
  if (data.persistence?.isOpen) {
    registerTooltip(el, data)
  }
}

const update = (el: HTMLElement, data: TmTooltipDirectiveData) => {
  const state = elStates.get(el)
  if (!state) {
    attach(el, data)
    return
  }

  state.data = data

  if (data.persistence) {
    el.removeEventListener('mouseenter', state.onMouseEnter)
  } else {
    el.addEventListener('mouseenter', state.onMouseEnter)
  }

  if (isRegisteredTooltip(el)) {
    updateTooltipData(el, data)
  }
  // if tooltip is not registered yet, but has persistence.isOpen=true during the updating, register the tooltip immediately
  // it may happen when the tooltip mounted with persistence.isOpen=false, and then updated with persistence.isOpen=true
  else if (data.persistence?.isOpen) {
    registerTooltip(el, data)
  }
}

const detach = (el: HTMLElement) => {
  const state = elStates.get(el)
  if (!state) return

  el.removeEventListener('mouseenter', state.onMouseEnter)
  if (isRegisteredTooltip(el)) {
    unregisterTooltip(el)
  }
  elStates.delete(el)
}

export const tooltipDirective: ObjectDirective<HTMLElement, TmTooltipDirectiveBinding> = {
  beforeMount(el: HTMLElement, binding: DirectiveBinding<TmTooltipDirectiveBinding>): void {
    if (el && binding.value) {
      attach(el, binding.value)
    }
  },
  updated(el: HTMLElement, binding: DirectiveBinding<TmTooltipDirectiveBinding>): void {
    if (!el) return
    if (binding.value) {
      update(el, binding.value)
    } else {
      detach(el)
    }
  },
  beforeUnmount(el: HTMLElement) {
    detach(el)
  },
}
